import * as React from 'react';
import { ChevronsUpDown, Check, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional description shown below the label */
  description?: string;
  /** Badge variant for selected items in the trigger */
  badgeVariant?: 'default' | 'secondary' | 'outline';
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Placeholder when nothing is selected */
  placeholder?: string;
  /** Classname for the trigger button */
  className?: string;
  /** Disable the entire component */
  disabled?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  className,
  disabled = false,
}: MultiSelectProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);

  const selectedOptions = options.filter((o) => selected.includes(o.value));

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const removeOption = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            // `h-auto` so chips that wrap to a second row stay inside the border;
            // the button's fixed `h-9` let them spill over whatever sat below.
            'h-auto min-h-9 w-full justify-between py-1.5 font-normal',
            selected.length === 0 && 'text-muted-foreground',
            className
          )}
        >
          <div className="flex flex-1 flex-wrap gap-1 overflow-hidden">
            {selectedOptions.length === 0 ? (
              <span>{placeholder}</span>
            ) : (
              selectedOptions.map((opt) => (
                <Badge
                  key={opt.value}
                  variant={opt.badgeVariant ?? 'secondary'}
                  className="shrink-0"
                >
                  {opt.label}
                  <button
                    type="button"
                    className="ml-1 rounded-full outline-none ring-offset-background hover:bg-foreground/20"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => removeOption(opt.value, e)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
        <div className="space-y-1">
          {options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
                  isSelected && 'bg-accent/50'
                )}
                onClick={() => toggleOption(option.value)}
              >
                <Checkbox checked={isSelected} className="pointer-events-none" tabIndex={-1} />
                <div className="flex flex-col items-start">
                  <span>{option.label}</span>
                  {option.description && (
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  )}
                </div>
                {isSelected && <Check className="ml-auto h-4 w-4 shrink-0" />}
              </button>
            );
          })}
          {options.length === 0 && (
            <p className="py-2 text-center text-sm text-muted-foreground">No options available</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
